import { Capacitor, registerPlugin } from '@capacitor/core';
import { config } from '../config.js';
import { AuthError } from './auth.js';

/**
 * Login com Google, sempre pela nossa API (o segredo do Cognito fica no servidor):
 *   navegador → vai para /v1/auth/google/start?platform=web e volta em app.ticketcar.com.br/entrar?c=...
 *   app       → abre o navegador do sistema (o Google não aceita login dentro de WebView) e volta pelo
 *               App Link ticketcar.com.br/app/entrar?c=... (ver app-links.js)
 * A tela /entrar troca o código de uso único pelos tokens.
 * Entrar com Apple: o mesmo caminho, com provider=apple (botão só aparece com a Apple ligada na API).
 */
const Browser = registerPlugin('Browser');
let enabledPromise = null;

export const google = {
  /** O que está ligado no Cognito deste ambiente: { google, apple }. */
  providers() {
    enabledPromise ??= fetch(`${config.apiUrl}/v1/auth/google/config`)
      .then((r) => (r.ok ? r.json() : { enabled: false }))
      .then((j) => ({ google: Boolean(j.enabled), apple: Boolean(j.enabled && j.apple) }))
      .catch(() => ((enabledPromise = null), { google: false, apple: false }));
    return enabledPromise;
  },

  /** Botão só aparece com o Google configurado no Cognito deste ambiente. */
  async enabled() {
    return (await this.providers()).google;
  },

  /** @param {'google' | 'apple'} [provider] */
  async start(provider = 'google') {
    const native = Capacitor.isNativePlatform();
    // iPhone: a API volta direto pelo esquema ticketcar:// (o Universal Link nem sempre abre o app nesse caminho)
    const platform = !native ? 'web' : Capacitor.getPlatform() === 'ios' ? 'ios' : 'app';
    const url = `${config.apiUrl}/v1/auth/google/start?platform=${platform}${provider === 'apple' ? '&provider=apple' : ''}`;
    if (native) await Browser.open({ url, presentationStyle: 'popover' });
    else window.location.assign(url);
  },

  /** Fecha a aba do navegador aberta pelo app (na volta do App Link). */
  closeBrowser() {
    if (Capacitor.isNativePlatform()) Browser.close().catch(() => {});
  },

  /** @returns {Promise<{ accessToken: string, refreshToken: string, expiresIn: number, email: string, username: string, name: string | null }>} */
  async exchange(code) {
    let res;
    try {
      res = await fetch(`${config.apiUrl}/v1/auth/google/exchange`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ code }) });
    } catch {
      throw new AuthError('NETWORK', 'Sem conexão. Verifique sua internet.', 0);
    }
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new AuthError(data?.error?.code ?? `HTTP_${res.status}`, data?.error?.message ?? 'Não foi possível entrar. Tente de novo.', res.status);
    return data;
  },
};

/** Mensagens dos motivos que a API manda em /entrar?erro=... */
export const GOOGLE_ERRORS = {
  cancelado: 'Login com Google cancelado.',
  expirou: 'O login demorou demais. Tente de novo.',
  falhou: 'Não foi possível entrar com o Google. Tente de novo.',
  'email-nao-verificado': 'Seu e-mail no Google não está verificado. Entre com e-mail e senha ou verifique o e-mail no Google.',
  bloqueada: 'Sua conta está bloqueada. Fale com o suporte.',
  'use-google': 'Este e-mail já tem conta que entra com Google. Use "Entrar com Google".',
  'use-apple': 'Este e-mail já tem conta que entra com Apple. Use "Continuar com a Apple".',
};

/** Botão no padrão do Google (logo "G" oficial em cores). */
export const googleButton = (label) => `
  <button type="button" class="google-btn" hidden>
    <svg viewBox="0 0 48 48" width="20" height="20" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
    <span>${label}</span>
  </button>
  <button type="button" class="apple-btn" hidden>
    <span class="apple-logo" aria-hidden="true">\uF8FF</span>
    <span>Continuar com a Apple</span>
  </button>
  <div class="or-sep" hidden><span>ou</span></div>`;

/**
 * O botão da Apple só existe no app iOS. Logo: o caractere U+F8FF vira o logo da Apple na fonte do iPhone.
 * (Para usar a arte oficial, troque pelo SVG do kit "Sign in with Apple" da Apple.)
 */
const iosApp = () => Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

/** Mostra os botões (Google e Apple, conforme o que estiver ligado) e liga os cliques. */
export function wireGoogleButton(page) {
  const btn = page.querySelector('.google-btn');
  const apple = page.querySelector('.apple-btn');
  const sep = page.querySelector('.or-sep');
  google.providers().then((on) => {
    // Apple só no app do iPhone (exigência da App Store); no navegador e no Android o botão não aparece
    const showApple = on.apple && iosApp();
    btn.hidden = !on.google;
    apple.hidden = !showApple;
    sep.hidden = !on.google && !showApple;
  });
  for (const [el, provider] of [[btn, 'google'], [apple, 'apple']]) {
    el.addEventListener('click', async () => {
      el.disabled = true;
      try {
        await google.start(provider);
      } finally {
        setTimeout(() => (el.disabled = false), 1500);
      }
    });
  }
}
